import { authManager, parseAccessToken } from 'lib-shared-auth';

export type AuthData = {
  /**
   * A short lived (currently 10 minutes) token for accessing private API
   * endpoints only.
   */
  readonly access_token: string;
  /**
   * A token which is used for requesting a new access token. Has a longer
   * lifespan (currently 7 days).
   */
  readonly refresh_token: string;
  /** The expiration (ttl) in milliseconds. */
  readonly expires_in: number;
};

/** The localStorage auth data key. */
const localStorageAuthDataKey = 'authData';

export async function getAuthData(): Promise<AuthData | null> {
  await authManager.getAuthData();
  const rawAuthData = localStorage.getItem(localStorageAuthDataKey);
  const authData = rawAuthData ? (JSON.parse(rawAuthData) as AuthData) : null;

  return authData;
}

/**
 * The current user's id, read from the stored access token.
 *
 * Synchronous (and so safe to call during render) because the id never changes
 * for a session — an expired token still carries the right one, so there is
 * nothing to refresh.
 */
export function getCurrentUserId(): string | null {
  const rawAuthData = localStorage.getItem(localStorageAuthDataKey);
  if (!rawAuthData) return null;

  try {
    const { access_token } = JSON.parse(rawAuthData) as AuthData;
    return parseAccessToken(access_token).user_id;
  } catch {
    return null;
  }
}

/** Creates the authorization header value. */
export async function createAuthorizationHeaderValue(): Promise<string> {
  const authData = await getAuthData();
  return `Bearer ${authData?.access_token}`;
}
